import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Home,
  Coins,
  LineChart,
  Gift,
  User,
  Eye,
  EyeOff,
  ShoppingCart,
  DollarSign,
  TrendingUp,
  Plus,
} from "lucide-react";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://alkebulanfkalertbackend.onrender.com/api";

// Helper to fetch live crypto prices from CoinGecko
async function fetchCryptoPrices(symbols = ["bitcoin", "ethereum", "tether"]) {
  const ids = symbols.join(",");
  const url = `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd&include_24hr_change=true`;
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new Error("Failed to fetch crypto prices");
  }
  return resp.json();
}

export default function WalletLunoPage() {
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSendModal, setShowSendModal] = useState(false);

  const [receiverEmail, setReceiverEmail] = useState("");
  const [walletAddress, setWalletAddress] = useState("luno");
  const [amount, setAmount] = useState("");
  const [coinType, setCoinType] = useState("USDT");
  const [sending, setSending] = useState(false);
  const [hideBalance, setHideBalance] = useState(false);

  const [prices, setPrices] = useState({});
  const [priceLoading, setPriceLoading] = useState(true);

  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");

  // Fetch balance + transactions
  useEffect(() => {
    const fetchBalanceAndHistory = async () => {
      try {
        const res = await axios.get(`${API_BASE}/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Dashboard mirrors the main account balance into every wallet.
        const lunoBalance = res.data.balance ?? res.data.balances?.luno ?? 0;
        setBalance(lunoBalance);

        // The backend has no GET /api/transactions list endpoint —
        // history renders empty until one is added.
        const txRes = { data: [] };

        setTransactions(Array.isArray(txRes.data) ? txRes.data : []);
      } catch (err) {
        console.error("⚠️ Error fetching Luno data:", err.message);
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBalanceAndHistory();
  }, [userId, token]);

  useEffect(() => {
  const loadPrices = async () => {
    try {
      const result = await fetchCryptoPrices(["bitcoin", "ethereum", "tether"]);
      setPrices(result);
    } catch (err) {
      console.error("Error fetching crypto prices", err);
      setPrices({});
    } finally {
      setPriceLoading(false);
    }
  };

  loadPrices();
}, []);


  // ✅ define it here
const handleSend = () => {
  // Sending from this wallet is disabled — the backend no longer
  // accepts debits from this page (it was an unauthenticated money
  // endpoint). Use the main transfer feature instead.
  setShowSendModal(false);
  alert("Sending from this wallet is no longer available. Please use the main transfer feature.");
};


  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-lg font-semibold">
        Loading Luno Wallet...
      </div>
    );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white px-4 pt-6 pb-4 shadow">
        <div className="flex items-center justify-between">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
            C
          </div>
          <button className="relative text-gray-500">
            <span className="absolute w-2 h-2 bg-red-500 rounded-full -mt-1 ml-2"></span>
            🔔
          </button>
        </div>

        <div className="mt-6">
          <p className="text-gray-500 text-sm">Your portfolio</p>
          <div className="flex items-center gap-2">
            <h1 className="text-4xl font-bold">
              {hideBalance
                ? "₦•••••"
                : `₦${Number(balance).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}`}
            </h1>
            <button onClick={() => setHideBalance(!hideBalance)}>
              {hideBalance ? (
                <EyeOff className="w-6 h-6 text-gray-500" />
              ) : (
                <Eye className="w-6 h-6 text-gray-500" />
              )}
            </button>
          </div>
          <p className="text-gray-500 text-xs">₦0.00 (0.00%) over the last day</p>
        </div>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-4 gap-4 px-6 py-4 bg-white">
        {[
          { icon: <ShoppingCart />, label: "Buy" },
          { icon: <DollarSign />, label: "Sell" },
          { icon: <TrendingUp />, label: "Earn" },
          { icon: <Plus />, label: "Send", action: () => setShowSendModal(true) },
        ].map((btn, i) => (
          <button
            key={i}
            onClick={btn.action}
            className="flex flex-col items-center"
          >
            <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center shadow">
              {btn.icon}
            </div>
            <span className="mt-2 text-sm">{btn.label}</span>
          </button>
        ))}
      </div>

      {/* Promo Card */}
      <div className="mx-6 mt-4 bg-white rounded-xl shadow p-4 flex items-center">
        <div className="flex-1">
          <h3 className="font-semibold text-sm">
            Your last chance to win $1,250
          </h3>
          <p className="text-xs text-gray-500">
            This week's Price Predict is about to close. Terms apply
          </p>
        </div>
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
          ⏰
        </div>
      </div>

      {/* Asset Prices */}
      <div className="mt-6 px-6">
        <div className="flex gap-3 text-sm font-medium overflow-x-auto">
          {["Favourites", "Crypto", "Stocks", "ETFs"].map((tab, i) => (
            <button
              key={i}
              className={`px-3 py-1 rounded-full whitespace-nowrap ${
                i === 0 ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="mt-4 space-y-3">
          {["bitcoin", "ethereum", "tether"].map((id) => {
            const item = prices[id];
            if (!item) {
              return (
                <div
                  key={id}
                  className="bg-white rounded-xl shadow p-4 flex justify-between items-center"
                >
                  <div>
                    <p className="font-semibold capitalize">{id}</p>
                    <p className="text-xs text-gray-500">{id.toUpperCase()}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">--</p>
                    <p className="text-xs text-gray-500">--</p>
                  </div>
                </div>
              );
            }
            const change = item.usd_24h_change;
            return (
              <div
                key={id}
                className="bg-white rounded-xl shadow p-4 flex justify-between items-center"
              >
                <div>
                  <p className="font-semibold capitalize">{id}</p>
                  <p className="text-xs text-gray-500">{id.toUpperCase()}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">${item.usd.toLocaleString()}</p>
                  <p
                    className={`text-xs ${
                      change >= 0 ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {change.toFixed(2)}%
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Transactions */}
      <div className="flex-1 px-6 mt-6">
        <h2 className="text-lg font-semibold mb-3">Recent Transactions</h2>
        <div className="bg-white rounded-xl shadow divide-y">
          {transactions.length === 0 ? (
            <p className="p-4 text-gray-500">No recent transactions</p>
          ) : (
            transactions.map((tx) => (
              <div
                key={tx._id}
                className="flex justify-between items-center p-4"
              >
                <div>
                  <p className="font-medium">
                    {tx.type === "debit" ? "Sent" : "Received"} ₦
                    {Number(tx.amount).toLocaleString()} ({tx.coinType || "USDT"})
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(tx.createdAt).toLocaleString()}
                  </p>
                </div>
                <span
                  className={`text-sm font-semibold ${
                    tx.status === "failed"
                      ? "text-red-600"
                      : "text-green-600"
                  }`}
                >
                  {tx.status}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Bottom Nav */}
      <div className="bg-white shadow-md flex justify-around py-3 mt-6">
        <button className="flex flex-col items-center text-blue-600">
          <Home className="w-6 h-6" />
          <span className="text-xs">Home</span>
        </button>
        <button className="flex flex-col items-center text-gray-500">
          <Coins className="w-6 h-6" />
          <span className="text-xs">Crypto</span>
        </button>
        <button className="flex flex-col items-center text-gray-500">
          <LineChart className="w-6 h-6" />
          <span className="text-xs">Stocks</span>
        </button>
        <button className="flex flex-col items-center text-gray-500">
          <Gift className="w-6 h-6" />
          <span className="text-xs">Rewards</span>
        </button>
        <button className="flex flex-col items-center text-gray-500">
          <User className="w-6 h-6" />
          <span className="text-xs">Portfolio</span>
        </button>
      </div>

      {/* Send Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-96">
            <h3 className="text-lg font-bold mb-4">Send Funds</h3>

            <label className="block text-sm mb-1">Receiver Email</label>
            <input
              type="email"
              value={receiverEmail}
              onChange={(e) => setReceiverEmail(e.target.value)}
              className="border rounded w-full p-2 mb-3"
              placeholder="example@email.com"
            />

            <label className="block text-sm mb-1">Wallet Address</label>
            <input
              type="text"
              value={walletAddress}
              onChange={(e) => setWalletAddress(e.target.value)}
              className="border rounded w-full p-2 mb-3"
              placeholder="Enter wallet address"
            />

            <label className="block text-sm mb-1">Amount</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="border rounded w-full p-2 mb-3"
              placeholder="₦0.00"
            />

            <label className="block text-sm mb-1">Coin Type</label>
            <input
              type="text"
              value={coinType}
              onChange={(e) => setCoinType(e.target.value)}
              className="border rounded w-full p-2 mb-4"
              placeholder="e.g., BTC, ETH, USDT"
            />

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowSendModal(false)}
                className="px-4 py-2 border rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                className="px-4 py-2 bg-gray-300 text-gray-600 rounded-lg flex items-center justify-center"
              >
                Not available
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


